import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Search, Filter, Grid, List } from 'lucide-react';
import { getAllProducts, getProductCategories } from '../data/productData';
import ProductCardSkeleton from '../components/common/ProductCardSkeleton';
import useSEO from '../hooks/useSEO';

const Products = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(12);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const categoryFromUrl = searchParams.get('category');
  const pageFromUrl = searchParams.get('page');

  const seoTitle = selectedCategory !== 'All'
    ? `${selectedCategory} Products`
    : 'Water Treatment Products';
  const seoDesc = selectedCategory !== 'All'
    ? `Browse Minipore ${selectedCategory} products. High-quality water treatment solutions with global shipping.`
    : 'Browse our complete catalog of water treatment products: RO membranes, UF membranes, filter housings, filter cartridges, and more. ISO9001 certified.';

  useSEO({
    title: seoTitle,
    description: seoDesc,
    keywords: `${selectedCategory !== 'All' ? selectedCategory + ', ' : ''}water treatment products, RO membrane, UF membrane, filter housing, Minipore`,
    url: '/products',
  });

  useEffect(() => {
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
    } else {
      setSelectedCategory('All');
    }
    if (pageFromUrl) {
      const page = parseInt(pageFromUrl, 10);
      if (page > 0) setCurrentPage(page);
    } else {
      setCurrentPage(1);
    }
  }, [categoryFromUrl, pageFromUrl]);

  useEffect(() => {
    const s = location.state;
    if (s && location.pathname === '/products') {
      if (typeof s.viewMode === 'string') setViewMode(s.viewMode);
      if (typeof s.searchTerm === 'string') setSearchTerm(s.searchTerm);
      if (typeof s.productsPerPage === 'number') setProductsPerPage(s.productsPerPage);
      if (typeof s.currentPage === 'number') setCurrentPage(s.currentPage);
      const newParams = new URLSearchParams();
      const cat = s.selectedCategory || 'All';
      if (cat && cat !== 'All') newParams.set('category', cat);
      const page = s.currentPage && s.currentPage > 1 ? s.currentPage.toString() : null;
      if (page) newParams.set('page', page);
      setSearchParams(newParams, { replace: true });
      if (typeof s.scrollY === 'number') {
        setTimeout(() => { window.scrollTo({ top: s.scrollY, behavior: 'auto' }); }, 50);
      }
    }
  }, [location.key]);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => { setIsLoading(false); }, 800);
    return () => clearTimeout(timer);
  }, [selectedCategory, searchTerm, currentPage, productsPerPage]);

  const updateUrlParams = (category, page) => {
    const newParams = new URLSearchParams(searchParams);
    if (category === 'All') { newParams.delete('category'); } else { newParams.set('category', category); }
    if (page === 1) { newParams.delete('page'); } else { newParams.set('page', page.toString()); }
    setSearchParams(newParams);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    updateUrlParams(category, 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlParams(selectedCategory, page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1);
      updateUrlParams(selectedCategory, 1);
    }
  }, [searchTerm]);

  const allProducts = useMemo(() => {
    try { return getAllProducts() || []; } catch (error) { return []; }
  }, []);

  const categories = useMemo(() => {
    try { return getProductCategories() || []; } catch (error) { return []; }
  }, []);

  const filteredProducts = useMemo(() => {
    try {
      let filtered = [...allProducts];
      if (selectedCategory !== 'All') {
        filtered = filtered.filter(p => p && p.category === selectedCategory);
      }
      if (searchTerm && searchTerm.trim()) {
        const s = searchTerm.toLowerCase().trim();
        filtered = filtered.filter(p => {
          if (!p) return false;
          return (p.name || '').toLowerCase().includes(s) ||
            (p.shortDescription || '').toLowerCase().includes(s) ||
            (p.description || '').toLowerCase().includes(s);
        });
      }
      return filtered || [];
    } catch (error) { return []; }
  }, [allProducts, selectedCategory, searchTerm]);

  const totalProducts = filteredProducts.length;
  const totalPages = Math.ceil(totalProducts / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, startIndex + productsPerPage);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  const categoryCount = (name) => {
    try {
      if (name === 'All') return allProducts.length;
      return allProducts.filter(p => p && p.category === name).length;
    } catch { return 0; }
  };

  const handleProductClick = (e, product) => {
    e.preventDefault();
    const from = `${location.pathname}${location.search}`;
    navigate(`/products/${product.id}`, {
      state: { from, viewMode, searchTerm, selectedCategory, currentPage, productsPerPage, scrollY: window.scrollY },
    });
  };

  const showCategoryCards = selectedCategory === 'All' && !searchTerm.trim();

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Our Products</h1>
          <p className="text-lg text-gray-600">Discover our comprehensive range of filtration and membrane solutions</p>
          {(selectedCategory !== 'All' || searchTerm.trim() !== '') && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedCategory !== 'All' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  Category: {selectedCategory}
                  <button onClick={() => handleCategoryChange('All')} className="ml-2 hover:text-blue-600">×</button>
                </span>
              )}
              {searchTerm.trim() !== '' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  Search: {searchTerm}
                  <button onClick={() => setSearchTerm('')} className="ml-2 hover:text-green-600">×</button>
                </span>
              )}
            </div>
          )}
        </div>

        {showCategoryCards && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
            {categories.slice(0, 10).map((category) => (
              <div
                key={category.id}
                className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all cursor-pointer border-2 border-transparent hover:border-blue-200 flex flex-col h-full"
                onClick={() => handleCategoryChange(category.name)}
              >
                <div className="flex-grow">
                  <h2 className="font-semibold mb-2 text-gray-900 text-base">{category.name}</h2>
                  <p className="text-xs text-gray-500 mb-3 line-clamp-2">{category.description}</p>
                </div>
                <div className="mt-auto border-t pt-3">
                  <span className="text-sm font-medium text-blue-600">{categoryCount(category.name)} products</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-gray-400" />
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="All">All Categories ({allProducts.length})</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name} ({categoryCount(category.name)})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show:</span>
                <select
                  value={productsPerPage}
                  onChange={(e) => { setProductsPerPage(Number(e.target.value)); setCurrentPage(1); updateUrlParams(selectedCategory, 1); }}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={6}>6</option>
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={48}>48</option>
                </select>
              </div>
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  <Grid className="h-5 w-5" />
                </button>
                <button onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  <List className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {!isLoading && (
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-gray-600">
              Showing {startIndex + 1}–{Math.min(startIndex + productsPerPage, totalProducts)} of {totalProducts} products
              {selectedCategory !== 'All' && ` in ${selectedCategory}`}
              {searchTerm && ` matching "${searchTerm}"`}
            </p>
            {totalPages > 1 && <p className="text-sm text-gray-500">Page {currentPage} of {totalPages}</p>}
          </div>
        )}

        {isLoading ? (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8' : 'space-y-6 mb-8'}>
            {Array.from({ length: productsPerPage }).map((_, i) => <ProductCardSkeleton key={i} viewMode={viewMode} />)}
          </div>
        ) : currentProducts.length > 0 ? (
          <>
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8' : 'space-y-6 mb-8'}>
              {currentProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  onClick={(e) => handleProductClick(e, product)}
                  className={`bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group ${viewMode === 'list' ? 'flex' : ''}`}
                >
                  <div className={viewMode === 'list' ? 'w-48 flex-shrink-0' : ''}>
                    <div className={`${viewMode === 'list' ? 'h-full' : 'aspect-w-16 aspect-h-9'} bg-gray-200 overflow-hidden`}>
                      <img
                        src={product.image || '/placeholder-image.jpg'}
                        alt={product.name || 'Product'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        onError={(e) => { e.target.src = '/placeholder-image.jpg'; }}
                      />
                    </div>
                  </div>
                  <div className={`p-6 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                    <div className="mb-2">
                      <span className="inline-block px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">
                        {product.category || 'Uncategorized'}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                      {product.name || 'Unnamed Product'}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {product.shortDescription || product.description || 'No description available'}
                    </p>
                    <div className="flex items-center text-blue-600 font-medium">Learn More</div>
                  </div>
                </Link>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2">
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`px-3 py-2 rounded-lg text-sm transition-colors ${currentPage === 1 ? 'text-gray-400' : 'text-gray-700 hover:bg-gray-100'}`}>Previous</button>
                {getPageNumbers().map((num, i) => (
                  <button key={i} onClick={() => typeof num === 'number' && handlePageChange(num)} className={`px-3 py-2 rounded-lg text-sm ${currentPage === num ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>{num}</button>
                ))}
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className={`px-3 py-2 rounded-lg text-sm transition-colors ${currentPage === totalPages ? 'text-gray-400' : 'text-gray-700 hover:bg-gray-100'}`}>Next</button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <button onClick={() => setSearchTerm('')} className="text-blue-600 underline">Clear search</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;